import { talents as allTalentsRaw } from '@/mocks';
import type { DigitalEmployee, JobCategory, AbilityTag } from '@/mocks';
import { salaryTiers, capabilityLibs, industryTags as staticIndustryTags, marketplaceEmployees as adminDefinedEmployees } from '@/mocks';
import {
  roleCategories as roleCategoriesMap,
  categoryIconMap,
  categoryColorMap,
  getRoleCategory as getCategory,
} from '@/mocks';

export { salaryTiers, capabilityLibs, staticIndustryTags };

// ─── 使用统一聚合入口（已含去重+name-override）──────────────────────────────
const allTalents: any[] = allTalentsRaw;

function parseSalaryNum(salary: string | number): number {
  if (typeof salary === 'number') return salary;
  const m = String(salary).replace(/,/g, '').match(/\d+/);
  return m ? parseInt(m[0]) : 0;
}

function talentToEmployee(t: any): DigitalEmployee {
  const salaryNum = parseSalaryNum(t.salary);
  const jobCat = getCategory(t.role);
  const prof = t.profile || {};

  let identity = '';
  let coreMission = '';
  let deliverySample = '';
  let successMetrics = '';

  if (typeof prof.identity === 'object' && prof.identity) {
    identity = prof.identity.roleName || prof.identity.personality || '';
  } else if (typeof prof.identity === 'string') {
    identity = prof.identity;
  }
  if (typeof prof.mission === 'object' && prof.mission) {
    coreMission = prof.mission.goals || prof.mission.duties || '';
  } else if (typeof prof.mission === 'string') {
    coreMission = prof.mission;
  }
  if (typeof prof.delivery === 'object' && prof.delivery) {
    deliverySample = prof.delivery.workflow || prof.delivery.example || '';
  }
  if (typeof prof.metrics === 'object' && prof.metrics) {
    successMetrics = prof.metrics.quality || prof.metrics.acceptance || '';
  }
  if (prof.qualityMetrics) {
    successMetrics = Array.isArray(prof.qualityMetrics) ? prof.qualityMetrics.join('；') : String(prof.qualityMetrics);
  }

  const workCases = Array.isArray(t.cases) ? t.cases.slice(0, 3).map((c: any, i: number) => ({
    id: `wc-${t.id}-${i}`,
    title: String(c.title || ''),
    industry: Array.isArray(c.tags) ? (c.tags[0] || '') : '',
    result: String(c.result || ''),
    description: `${c.title}：${c.result || ''}`,
    date: String(c.date || c.duration || '2024-01').slice(0, 7),
  })) : [];

  const bossReviews = Array.isArray(t.reviews) ? t.reviews.slice(0, 3).map((r: any, i: number) => ({
    id: `br-${t.id}-${i}`,
    boss: String(r.name || r.author || '企业主'),
    company: String(r.company || ''),
    avatar: `https://readdy.ai/api/search-image?query=professional%20business%20executive%20headshot%20clean%20background%20portrait&width=40&height=40&seq=boss-${t.id}-${i}&orientation=squarish`,
    rating: Number(r.rating) || 5,
    content: String(r.content || ''),
    date: String(r.date || '2024-01-01'),
  })) : [];

  const scores = Array.isArray(t.skills) ? t.skills.map((s: any) => ({
    dimension: String(s.name),
    score: Number(s.level),
    maxScore: 100,
  })) : [];

  return {
    id: String(t.id),
    name: t.name,
    avatar: t.avatar,
    jobCategory: jobCat,
    title: t.role,
    gender: 'neutral' as const,
    introduction: String(t.description || ''),
    personality: (typeof prof.identity === 'object' && prof.identity) ? (prof.identity.personality || '') : '',
    salary: salaryNum,
    salaryUnit: 'month' as const,
    commission: 0,
    status: 'published' as const,
    industries: [],
    abilityTags: Array.isArray(t.tags) ? t.tags : [],
    trialDays: 3,
    identity,
    coreMission,
    deliverySample,
    successMetrics,
    scores,
    performance: {
      totalTasks: 800 + (Number(t.id) % 8000),
      satisfactionRate: 90 + (Number(t.id) % 9),
      avgResponseTime: '30s',
      onlineHours: 8760,
    },
    workCases,
    bossReviews,
    factoryCapabilities: Array.isArray(t.tags) ? t.tags.slice(0, 5) : [],
    dependencyLibs: [],
    hasPerformanceReport: bossReviews.length > 0,
    hasWorkReport: workCases.length > 0,
    hasGrowthReport: workCases.length > 1,
    createdAt: '2024-01-01',
    updatedAt: '2026-03-22',
  };
}

// ─── categoryIconMap / categoryColorMap 已移至 mocks/roleCategories.ts ─────

// ─── ⚡ 全部改为懒计算：模块 import 时零开销，首次访问时才执行 ──────────────
// 这样 React.lazy 加载 EmployeesPanel 时，模块评估本身极快，
// 骨架屏可以在任何重量级计算开始前就先渲染出来。

/** 懒计算：去重后的 talents 列表 */
let _dedupedTalents: any[] | null = null;
function getDedupedTalents(): any[] {
  if (_dedupedTalents) return _dedupedTalents;
  const seenIds = new Set<string>();
  _dedupedTalents = [];
  allTalents.forEach((t: any) => {
    const sid = String(t.id);
    if (!seenIds.has(sid)) {
      seenIds.add(sid);
      _dedupedTalents!.push(t);
    }
  });
  return _dedupedTalents;
}

/** 懒计算：岗位分类计数 */
let _categoryCounts: Record<string, number> | null = null;
function getCategoryCounts(): Record<string, number> {
  if (_categoryCounts) return _categoryCounts;
  _categoryCounts = {};
  getDedupedTalents().forEach((t: any) => {
    const cat = getCategory(t.role);
    _categoryCounts![cat] = (_categoryCounts![cat] || 0) + 1;
  });
  return _categoryCounts;
}

/** 懒计算：能力标签计数 */
let _tagCountMap: Record<string, number> | null = null;
function getTagCountMap(): Record<string, number> {
  if (_tagCountMap) return _tagCountMap;
  _tagCountMap = {};
  getDedupedTalents().forEach((t: any) => {
    if (Array.isArray(t.tags)) {
      t.tags.forEach((tag: string) => {
        _tagCountMap![tag] = (_tagCountMap![tag] || 0) + 1;
      });
    }
  });
  return _tagCountMap;
}

// ─── 导出：岗位分类（懒初始化函数 + 向后兼容 Proxy）─────────────────────────
let _syncedJobCategoriesArr: JobCategory[] | null = null;

/** 获取岗位分类列表（懒初始化，首次调用时才执行计算） */
export function getSyncedJobCategories(): JobCategory[] {
  if (_syncedJobCategoriesArr) return _syncedJobCategoriesArr;
  const counts = getCategoryCounts();
  _syncedJobCategoriesArr = Object.entries(roleCategoriesMap).map(([name], idx) => ({
    id: `jc-sync-${idx + 1}`,
    name,
    icon: categoryIconMap[name] || 'ri-star-line',
    color: categoryColorMap[name] || '#94a3b8',
    sort: idx + 1,
    employeeCount: counts[name] || 0,
    description: `${counts[name] || 0} 位数字员工`,
    enabled: true,
  }));
  return _syncedJobCategoriesArr;
}

/**
 * @deprecated 请改用 getSyncedJobCategories()，该导出保留仅为向后兼容。
 * 使用 Proxy 确保访问时才触发懒初始化，不会在模块 import 阶段执行任何计算。
 */
export const syncedJobCategories: JobCategory[] = new Proxy([] as JobCategory[], {
  get(_target, prop) {
    const arr = getSyncedJobCategories();
    if (prop === 'length') return arr.length;
    if (prop === Symbol.iterator) return arr[Symbol.iterator].bind(arr);
    if (typeof prop === 'string' && !isNaN(Number(prop))) return arr[Number(prop)];
    const val = arr[prop as keyof JobCategory[]];
    if (typeof val === 'function') return (val as Function).bind(arr);
    return val;
  },
  has(_target, prop) { return prop in getSyncedJobCategories(); },
});

// ─── 导出：能力标签（懒初始化函数 + 向后兼容 Proxy）─────────────────────────
let _syncedAbilityTagsArr: AbilityTag[] | null = null;

/** 获取能力标签列表（懒初始化，首次调用时才执行计算） */
export function getSyncedAbilityTags(): AbilityTag[] {
  if (_syncedAbilityTagsArr) return _syncedAbilityTagsArr;
  const tagCountMap = getTagCountMap();
  _syncedAbilityTagsArr = Object.entries(tagCountMap)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 120)
    .map(([name, count], idx) => ({
      id: `at-sync-${idx + 1}`,
      name,
      category: '能力标签',
      color: '#0ea5e9',
      sort: idx + 1,
      usageCount: count,
      description: name,
    }));
  return _syncedAbilityTagsArr;
}

/**
 * @deprecated 请改用 getSyncedAbilityTags()，该导出保留仅为向后兼容。
 * 使用 Proxy 确保访问时才触发懒初始化，不会在模块 import 阶段执行任何计算。
 */
export const syncedAbilityTags: AbilityTag[] = new Proxy([] as AbilityTag[], {
  get(_target, prop) {
    const arr = getSyncedAbilityTags();
    if (prop === 'length') return arr.length;
    if (prop === Symbol.iterator) return arr[Symbol.iterator].bind(arr);
    if (typeof prop === 'string' && !isNaN(Number(prop))) return arr[Number(prop)];
    const val = arr[prop as keyof AbilityTag[]];
    if (typeof val === 'function') return (val as Function).bind(arr);
    return val;
  },
  has(_target, prop) { return prop in getSyncedAbilityTags(); },
});

// ─── 导出：数字员工（与用户端人才库同步）────────────────────────────────────
let _syncedEmployees: DigitalEmployee[] | null = null;

/**
 * 管理端 jobCategory 简称 → 用户端完整分类名映射（与 employeeStore 保持一致）
 */
const ADMIN_CAT_MAP: Record<string, string> = {
  '客服': '客服售后类', '售后': '客服售后类', '客服售后': '客服售后类',
  '销售': '销售市场类', '市场': '销售市场类', '销售市场': '销售市场类',
  '运营': '数据运营类', '数据': '数据运营类', '数据运营': '数据运营类',
  '技术': '技术开发类', '开发': '技术开发类', '技术开发': '技术开发类',
  '设计': '创意设计类', '创意': '创意设计类', '创意设计': '创意设计类',
  '法务': '法务合规类', '合规': '法务合规类', '法务合规': '法务合规类',
  '人事': '人力资源类', '人力': '人力资源类', '人力资源': '人力资源类',
  '产品': '产品设计类', '产品设计': '产品设计类',
  '媒体': '媒体运营类', '新媒体': '媒体运营类', '媒体运营': '媒体运营类',
  '项目': '项目管理运营类', '项目管理': '项目管理运营类',
  '生产': '生产管理岗', '生产管理': '生产管理岗',
};

function normalizeJobCategory(cat: string): string {
  if (cat.endsWith('类') || cat.endsWith('岗')) return cat;
  return ADMIN_CAT_MAP[cat] ?? cat;
}

/** 获取所有数字员工（懒初始化，首次调用时才执行对象构造） */
export function getSyncedEmployees(): DigitalEmployee[] {
  if (_syncedEmployees) return _syncedEmployees;
  const ft = getDedupedTalents().map(t => ({ ...talentToEmployee(t), id: String(t.id) }));
  const ftIds = new Set(ft.map(e => e.id));
  // 规范化 adminDefinedEmployees 的 jobCategory，确保与用户端分类名一致
  const adminOnly = adminDefinedEmployees
    .filter(e => !ftIds.has(e.id))
    .map(e => ({ ...e, jobCategory: normalizeJobCategory(e.jobCategory) }));
  _syncedEmployees = [...adminOnly, ...ft];
  return _syncedEmployees;
}

/**
 * @deprecated 请改用 getSyncedEmployees()，该导出保留仅为向后兼容。
 * 直接访问此变量时仍会触发懒初始化。
 */
export const syncedEmployees: DigitalEmployee[] = new Proxy([] as DigitalEmployee[], {
  get(_target, prop) {
    const arr = getSyncedEmployees();
    if (prop === 'length') return arr.length;
    if (prop === Symbol.iterator) return arr[Symbol.iterator].bind(arr);
    if (typeof prop === 'string' && !isNaN(Number(prop))) return arr[Number(prop)];
    const val = arr[prop as keyof DigitalEmployee[]];
    if (typeof val === 'function') return val.bind(arr);
    return val;
  },
  has(_target, prop) { return prop in getSyncedEmployees(); },
});
